import React, { useState, useEffect } from 'react';
import { User, Mail, Phone, CreditCard, Gift, Users, Info } from 'lucide-react';
import { validateEmail, formatAndValidateSACellphone, validateSAIDNumber } from './validationUtils';

const CustomerInfoStep = ({ formData, updateFormData, errors = {} }) => {
  const [idInfo, setIdInfo] = useState(null);
  const [localErrors, setLocalErrors] = useState({});
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    let updatedValue = value;
    let updatedFormData = { ...formData };
    
    // Special handling for cellphone - auto format
    if (name === 'cellphone') {
      const result = formatAndValidateSACellphone(value);
      updatedValue = result.formatted;
      
      // Update local validation errors
      if (!result.valid && value.length > 0) {
        setLocalErrors(prev => ({ ...prev, cellphone: result.message }));
      } else {
        setLocalErrors(prev => ({ ...prev, cellphone: null }));
      }
    }
    
    // Email validation
    if (name === 'email') {
      const result = validateEmail(value);
      if (!result.valid && value.length > 0) {
        setLocalErrors(prev => ({ ...prev, email: result.message }));
      } else {
        setLocalErrors(prev => ({ ...prev, email: null }));
      }
    }
    
    // ID Number validation
    if (name === 'idNumber') {
      if (value.length === 13) {
        const result = validateSAIDNumber(value, formData.title);
        if (!result.valid) {
          setLocalErrors(prev => ({ ...prev, idNumber: result.message }));
          setIdInfo(null);
        } else {
          setIdInfo(result.info);
          
          // Check for gender mismatch
          if (result.genderMismatch) {
            setLocalErrors(prev => ({ ...prev, idNumber: result.genderMismatchMessage }));
          } else {
            setLocalErrors(prev => ({ ...prev, idNumber: null }));
          }
        }
      } else if (value.length > 0) {
        setLocalErrors(prev => ({ ...prev, idNumber: 'ID number must be 13 digits' }));
        setIdInfo(null);
      } else {
        setLocalErrors(prev => ({ ...prev, idNumber: null }));
        setIdInfo(null);
      }
    }
    
    // Title change - validate against ID number if available
    if (name === 'title' && formData.idNumber && formData.idNumber.length === 13) {
      const result = validateSAIDNumber(formData.idNumber, value);
      if (result.valid && result.genderMismatch) {
        setLocalErrors(prev => ({ ...prev, idNumber: result.genderMismatchMessage }));
      } else if (result.valid) {
        setLocalErrors(prev => ({ ...prev, idNumber: null }));
      }
    }
    
    updatedFormData[name] = updatedValue;
    updateFormData(updatedFormData);
  };
  
  // Handle title selection
  const handleTitleSelect = (title) => {
    const updatedFormData = { ...formData, title };
    updateFormData(updatedFormData);
    
    // Validate against ID number if available
    if (formData.idNumber && formData.idNumber.length === 13) {
      const result = validateSAIDNumber(formData.idNumber, title);
      if (result.valid && result.genderMismatch) {
        setLocalErrors(prev => ({ ...prev, idNumber: result.genderMismatchMessage }));
      } else if (result.valid) {
        setLocalErrors(prev => ({ ...prev, idNumber: null }));
      }
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-white">Customer Details</h2>
      <p className="text-gray-400 text-sm">Please provide your personal information to create your account.</p>
      
      {/* Title Selection */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-300">Title</label>
        <div id="title" className="flex flex-wrap gap-2">
          {['Mr', 'Mrs', 'Ms', 'Dr'].map((title) => (
            <button
              key={title}
              type="button"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                formData.title === title
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
              onClick={() => handleTitleSelect(title)}
            >
              {title}
            </button>
          ))}
        </div>
        {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
      </div>
      
      {/* Name Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label htmlFor="firstName" className="block text-sm font-medium text-gray-300">First Name</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <User className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName || ''}
              onChange={handleChange}
              className={`w-full py-2 pl-10 pr-3 bg-gray-700 border ${
                errors.firstName ? 'border-red-500' : 'border-gray-600'
              } rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500`}
              placeholder="Your first name"
            />
          </div>
          {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
        </div>
        
        <div className="space-y-2">
          <label htmlFor="surname" className="block text-sm font-medium text-gray-300">Surname</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <User className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              id="surname"
              name="surname"
              value={formData.surname || ''}
              onChange={handleChange}
              className={`w-full py-2 pl-10 pr-3 bg-gray-700 border ${
                errors.surname ? 'border-red-500' : 'border-gray-600'
              } rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500`}
              placeholder="Your surname"
            />
          </div>
          {errors.surname && <p className="text-red-500 text-xs mt-1">{errors.surname}</p>}
        </div>
      </div>
      
      {/* Contact Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label htmlFor="email" className="block text-sm font-medium text-gray-300">Email Address</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Mail className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email || ''}
              onChange={handleChange}
              className={`w-full py-2 pl-10 pr-3 bg-gray-700 border ${
                errors.email || localErrors.email ? 'border-red-500' : 'border-gray-600'
              } rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500`}
              placeholder="your.email@example.com"
            />
          </div>
          {(errors.email || localErrors.email) && (
            <p className="text-red-500 text-xs mt-1">{errors.email || localErrors.email}</p>
          )}
        </div>
        
        <div className="space-y-2">
          <label htmlFor="cellphone" className="block text-sm font-medium text-gray-300">Cellphone</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Phone className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="tel"
              id="cellphone"
              name="cellphone"
              value={formData.cellphone || ''}
              onChange={handleChange}
              className={`w-full py-2 pl-10 pr-3 bg-gray-700 border ${
                errors.cellphone || localErrors.cellphone ? 'border-red-500' : 'border-gray-600'
              } rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500`}
              placeholder="South African cellphone number"
            />
          </div>
          {(errors.cellphone || localErrors.cellphone) && (
            <p className="text-red-500 text-xs mt-1">{errors.cellphone || localErrors.cellphone}</p>
          )}
          <p className="text-gray-400 text-xs">Format: 0834637609</p>
        </div>
      </div>
      
      {/* ID Number */}
      <div className="space-y-2">
        <label htmlFor="idNumber" className="block text-sm font-medium text-gray-300">ID Number</label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <CreditCard className="h-4 w-4 text-gray-400" />
          </div>
          <input
            type="text"
            id="idNumber"
            name="idNumber"
            value={formData.idNumber || ''}
            onChange={handleChange}
            className={`w-full py-2 pl-10 pr-3 bg-gray-700 border ${
              errors.idNumber || localErrors.idNumber ? 'border-red-500' : 'border-gray-600'
            } rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500`}
            placeholder="South African ID number"
            maxLength={13}
          />
        </div>
        {(errors.idNumber || localErrors.idNumber) && (
          <p className="text-red-500 text-xs mt-1">{errors.idNumber || localErrors.idNumber}</p>
        )}
        
        {/* ID Information Display */}
        {idInfo && (
          <div className="mt-2 p-3 bg-gray-800 rounded-lg">
            <div className="flex items-start">
              <Info className="h-4 w-4 text-purple-400 mr-2 mt-0.5" />
              <div className="text-xs text-gray-300">
                <p className="font-medium text-purple-300 mb-1">ID Information</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  <p>Date of Birth: <span className="text-white">{idInfo.dateOfBirth}</span></p>
                  <p>Age: <span className="text-white">{idInfo.age}</span></p>
                  <p>Gender: <span className="text-white">{idInfo.gender}</span></p>
                  <p>Citizenship: <span className="text-white">{idInfo.citizenship}</span></p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Optional Fields */}
      <div className="pt-4 border-t border-gray-700">
        <h3 className="text-md font-medium text-gray-300 mb-4">Additional Information (Optional)</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label htmlFor="promoCode" className="block text-sm font-medium text-gray-300">Promo Code</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Gift className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                id="promoCode"
                name="promoCode"
                value={formData.promoCode || ''}
                onChange={handleChange}
                className="w-full py-2 pl-10 pr-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Enter promo code if you have one"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <label htmlFor="referralCode" className="block text-sm font-medium text-gray-300">Referral Code</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Users className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                id="referralCode"
                name="referralCode"
                value={formData.referralCode || ''}
                onChange={handleChange}
                className="w-full py-2 pl-10 pr-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Enter referral code if you have one"
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Existing Customer */}
      <div className="space-y-2 pt-4 border-t border-gray-700">
        <label className="block text-sm font-medium text-gray-300">Are you an existing customer?</label>
        <div id="existingCustomer" className="flex gap-4">
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="existingCustomer"
              value="yes"
              checked={formData.existingCustomer === 'yes'}
              onChange={handleChange}
              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-600 bg-gray-700"
            />
            <span className="ml-2 text-gray-300">Yes</span>
          </label>
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="existingCustomer"
              value="no"
              checked={formData.existingCustomer === 'no'}
              onChange={handleChange}
              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-600 bg-gray-700"
            />
            <span className="ml-2 text-gray-300">No</span>
          </label>
        </div>
        {errors.existingCustomer && <p className="text-red-500 text-xs mt-1">{errors.existingCustomer}</p>}
      </div>
    </div>
  );
};

export default CustomerInfoStep;